/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { useState, useEffect } from 'react'
import { Alert, Box, Grid } from '@mui/material'
import { type carenetworkService } from 'core/types'
import { sendGet } from 'hooks/use-fetch'
import { NotAllowed } from 'Routes/ProtectedRoute'
import { SelectWithLabel } from 'Components/SelectWithLabel'
import { EnrollmentDetails } from 'Components/Enrollment/EnrollmentDetails'
import { NewServiceSelection } from 'Components/Services/NewServiceSelection'
import { useRefresh } from 'ContextProviders/RefreshProvider'
import { InfoOutlined } from '@mui/icons-material'

enum ServiceStatus {
  Active = 'Active',
  Inactive = 'Inactive',
  All = 'All'
}

const statusOptions = [
  { id: ServiceStatus.Active, name: ServiceStatus.Active },
  { id: ServiceStatus.Inactive, name: ServiceStatus.Inactive },
  { id: ServiceStatus.All, name: ServiceStatus.All }
]

export interface CareNetworkServicesProps {
  childID: number
  childName?: string
  districtId: number
  isdId: number
  readonly: boolean
}

export function CareNetworkServices (props: CareNetworkServicesProps): JSX.Element {
  const [services, setServices] = useState<carenetworkService[]>([])
  const refreshTime = useRefresh()
  const [unauthorized, setUnauthorized] = useState(false)
  const [allowAddService, setAllowAddService] = useState<boolean | null>(null)
  const [selectedStatus, setSelectedStatus] = useState<ServiceStatus>(ServiceStatus.Active)

  useEffect(() => {
    async function fetchAllowAddService (): Promise<void> {
      const { response, success } = await sendGet(`/ChildDetails/AllowAddService?id=${props.childID}`, {})
      if (success) {
        setAllowAddService(response)
      }
    }

    void fetchAllowAddService()
  }, [props.childID])

  useEffect(() => {
    async function getServices (): Promise<void> {
      const { response, success, status } = await sendGet(`/ChildDetails/GetServices?id=${props.childID}&isdid=${props.isdId}&status=${selectedStatus}&includeReferral=true`, {})
      if (success) {
        setServices(response.response)
      } else {
        if (status === 401) { setUnauthorized(true) }
      }
    }
    void getServices()
  }, [refreshTime, selectedStatus])

  if (unauthorized) return <NotAllowed />

  return <Box>
    {allowAddService === false && <Alert severity='warning' icon={<InfoOutlined />} sx={{ border: '1px solid', borderColor: 'gold', mt: 1 }}>This child needs to have an address and a financially responsible caregiver to create a service.</Alert>}
    <Box className='d-flex f-justify-content-space-between'>
      <SelectWithLabel
        label='Status'
        name='status'
        onChange={(e) => { setSelectedStatus(e.target.value as ServiceStatus) }}
        options={statusOptions}
        value={selectedStatus}
        width='200px'
      />
      <Box className='d-flex f-direction-column'>
        {allowAddService === true && !props.readonly && <NewServiceSelection childID={props.childID} isdID={props.isdId} />}
      </Box>
    </Box>
    <Grid container spacing={2} sx={{ m: 'auto', maxWidth: '1000px' }}>
      {services.map((service) => (
        <Grid item key={service.serviceID} xs={12} sm={6} lg={4}>
          <EnrollmentDetails enrollmentID={service.enrollmentID ?? 0} canReview={!props.readonly} fromChildID={props.childID} />
        </Grid>
      ))}
    </Grid>

  </Box>
}
